import * as React from 'react'

import { Button } from '~ui/components/buttons/Button/Button'
import { ButtonIcon } from '~ui/components/buttons/ButtonIcon/ButtonIcon'
import { AddAmountButtonGroup } from '~ui/components/formElements/AddAmountButton/AddAmountButtonGroup'
import { InputCheckboxGroup } from '~ui/components/formElements/Checkbox/InputCheckboxGroup'
import { InputRadioGroup } from '~ui/components/formElements/Radio/InputRadioGroup'
import { Icon } from '~ui/components/icons/Icon'
import { Flex, FlexCentered, Grid, GridRow } from '~ui/components/layout/Box'
import { Stack } from '~ui/components/layout/Stack'
import { Skeleton } from '~ui/components/loadingSkeletons/Skeleton'
import { H3 } from '~ui/components/typography/Heading/Heading'
import { Text } from '~ui/components/typography/Text/Text'

import { IconInfo } from '../../icons/specific/IconInfo'
import { IconClearCircle } from '../../icons/specific/IconX'
import { AddonValidationMessage } from './AddonValidationMessage'
import { getInputType, getMessageParameters } from './helpers'

type TStatus = PropsOf<typeof AddonValidationMessage>['status']
type TValidationMessage = PropsOf<typeof AddonValidationMessage>['validationMessage']

type TAddon = {
  id: ID
  amount: number
  nameLabel: string
  isMainProduct: boolean
  price: {
    value: number
    formattedValue: string
  }
}

type TCountOptions = {
  /**
   * Minimum number of selected addons.
   * Meant as sum of all individual counts:  *2x olives, 1x cheese -> totalCount === 3*
   */
  minTotalCount: number
  /**
   * Maximum number of selected addons.
   * Meant as sum of all individual counts:  *2x olives, 1x cheese -> totalCount === 3*
   */
  maxTotalCount: number
  /**
   * maximum amount of each individual addon
   */
  maxIndividualCount: number
}

type TProps = NoChildren &
  TCountOptions &
  SkeletonProps & {
    /**
     * function that gets message-type string and returns the message to inform the user about the category requirements
     */
    getValidationMessage: (meta: ReturnType<typeof getMessageParameters>) => TValidationMessage
    /**
     * translated text of the button that clears radio button group selection
     */
    cancelSelectionLabel: string
    /**
     * translated text of the button that shows item details
     */
    moreInfoLabel: string
    /**
     * addon category heading
     */
    nameLabel: string
    /**
     * array of addon objects that the controls (radios/checkboxes/add-amount buttons) should be rendered for
     */
    addonState: RoA<TAddon>
    /**
     * if true, render bottom border line under each item
     */
    hasDivider?:
      | PropsOf<typeof InputRadioGroup>['hasDivider']
      | PropsOf<typeof InputCheckboxGroup>['hasDivider']
      | PropsOf<typeof AddAmountButtonGroup>['hasDivider']
    /**
     * vertical space between individual addon items
     */
    spacing?: PropsOf<typeof Stack>['spacing']
    /**
     * min height of the individual addon item, it is also used to align the content of the items with multiple-line labels
     */
    minAddonItemHeight?: PropsOf<typeof Flex>['minHeight']
    /**
     * callback on an addon selection OR any change of addon amount in general
     */
    onSelect: (selectedAddons: RoA<TAddon>) => void
    /**
     * indicator whether it is possible to add more addons, if radio category is required,
     * canAdd is ignored until minimum number of addons (minCount) is selected
     */
    canAdd?: boolean
    /**
     * makes the whole category not interactive (eg. when submitting)
     */
    isDisabled?: boolean
    /**
     * For addons that are also main products the detail can be shown by clicking button icon rendered within the addon. Provide `onAddonDetailClick` handler in that case.
     */
    onAddonDetailClick?: (addon: TAddon) => void
  }

type TRenderLabelOptions = {
  withPrice: boolean
}

type TRenderPriceOptions = {
  contrastLevel: PropsOf<typeof Text>['contrast']
}

const BOOL_SELECTED = 1
const BOOL_UNSELECTED = 0
const NOT_SET = 0

export const AddonCategory: React.FC<TProps> = ({
  minAddonItemHeight = `2.5rem`,
  getValidationMessage,
  nameLabel,
  addonState,
  hasDivider,
  spacing = `extraSmall`,
  minTotalCount,
  maxTotalCount,
  maxIndividualCount,
  onSelect,
  canAdd = true,
  isDisabled = false,
  cancelSelectionLabel,
  moreInfoLabel,
  showSkeleton = false,
  onAddonDetailClick,
}) => {
  // if true, selection from this category is mandatory
  const isRequired = Boolean(minTotalCount && minTotalCount > 0)
  const selectedAddonCount = addonState.reduce((sum, addon) => sum + addon.amount, 0)

  // set amount of all the addons to 0, used to uncheck selected radio buttons
  const resetSelection = () => {
    const noSelection = addonState.map((addon) => ({ ...addon, amount: BOOL_UNSELECTED }))
    onSelect(noSelection)
  }

  // set amount of the selected item to 1, others to 0
  const selectRadioOption = (selectedAddon: TAddon) => {
    const newSelection = addonState.map((addon) => {
      return addon.id === selectedAddon.id
        ? { ...addon, amount: BOOL_SELECTED } //
        : { ...addon, amount: BOOL_UNSELECTED }
    })

    onSelect(newSelection)
  }

  // set amount of all selected (checked) items to 1, unselected to 0
  const handleCheckboxSelectionChange = (selectedAddons: RoA<TAddon>) => {
    const newSelection = addonState.map((addon) => {
      const isSelected = Boolean(
        selectedAddons.find((selectedAddon) => addon.id === selectedAddon.id),
      )

      return isSelected
        ? { ...addon, amount: BOOL_SELECTED } //
        : { ...addon, amount: BOOL_UNSELECTED }
    })

    onSelect(newSelection)
  }

  // radios and checkboxes have price labels on different place than add-amount buttons, they also use different contrasts
  const renderAddonPrice =
    ({ contrastLevel }: TRenderPriceOptions) =>
    (addon: TAddon) => (
      <Text contrast={contrastLevel} fontSize={`s14`}>
        {addon.price.formattedValue}
      </Text>
    )

  const renderAddonLabel =
    ({ withPrice }: TRenderLabelOptions) =>
    (addon: TAddon) => (
      <Grid
        height={`100%`}
        justifyContent={`space-between`}
        alignItems={`start`}
        gridTemplateColumns={
          addon.isMainProduct && withPrice ? `1fr auto auto auto` : `1fr auto auto`
        }
        width={`auto`}
        columnGap={`small`}
      >
        <Flex minHeight={minAddonItemHeight} alignItems={`center`}>
          <Skeleton showSkeleton={showSkeleton}>
            <Text fontSize={`s14`}>{addon.nameLabel}</Text>
          </Skeleton>
        </Flex>
        {addon.isMainProduct && onAddonDetailClick && (
          <FlexCentered height={minAddonItemHeight}>
            <Skeleton showSkeleton={showSkeleton} radius={`rounded`}>
              <ButtonIcon
                icon={IconInfo}
                iconSize={`1.4rem`}
                variant={`ghost`}
                shadow={`none`}
                iconContrast={`placeholder`}
                state={isDisabled ? `disabled` : `enabled`}
                onClick={() => onAddonDetailClick(addon)}
                padding={`none`}
                title={moreInfoLabel}
              />
            </Skeleton>
          </FlexCentered>
        )}
        {withPrice && (
          <FlexCentered height={minAddonItemHeight} textAlign={`right`}>
            {renderAddonPrice({ contrastLevel: `low` })(addon)}
          </FlexCentered>
        )}
      </Grid>
    )

  const inputType = getInputType({
    maxIndividualCount,
    maxTotalCount,
  })

  // decide what type of inputs the items will show (based on inputType: 'radio'|'checkbox'|'amount')
  const renderAddonGroup = () => {
    const idPropKey: keyof TAddon = `id`

    const commonProps = {
      idPropKey,
      spacing,
      hasDivider,
      options: addonState,
      minHeight: minAddonItemHeight,
    }

    const canAddMore = Boolean(canAdd && (!maxTotalCount || selectedAddonCount < maxTotalCount))

    switch (inputType) {
      case `radio`:
        return (
          <InputRadioGroup
            {...commonProps}
            groupName={`addons`}
            onSelect={selectRadioOption}
            renderOptionLabel={renderAddonLabel({ withPrice: true })}
            isDisabled={isDisabled || (!isRequired && selectedAddonCount === 0 && !canAdd)}
            selectedOption={addonState.find((addon) => addon.amount === BOOL_SELECTED) ?? null}
            cancelButton={
              !isRequired &&
              selectedAddonCount > 0 && (
                <Button
                  height={minAddonItemHeight}
                  variant={`ghost`}
                  padding={`none`}
                  shadow={`none`}
                  state={isDisabled ? `disabled` : `enabled`}
                  onClick={resetSelection}
                  width={`min-content`}
                >
                  <GridRow
                    columnGap={`small`}
                    alignItems={`center`}
                    justifyContent={`left`}
                    width={`100%`}
                    whiteSpace={`nowrap`}
                  >
                    <Icon icon={IconClearCircle} contrast={`low`} iconSize={`1.25rem`} />
                    <Text fontSize={`s14`} contrast={`low`}>
                      {cancelSelectionLabel}
                    </Text>
                  </GridRow>
                </Button>
              )
            }
          />
        )

      case `checkbox`:
        return (
          <InputCheckboxGroup
            {...commonProps}
            canAdd={canAddMore}
            isDisabled={isDisabled}
            onSelect={handleCheckboxSelectionChange}
            renderOptionLabel={renderAddonLabel({ withPrice: true })}
            selectedOptions={addonState.filter((addon) => addon.amount === BOOL_SELECTED)}
            checkboxMarginTop={`0.7rem`}
          />
        )

      case `amount`:
        return (
          <AddAmountButtonGroup
            {...commonProps}
            showSkeleton={showSkeleton}
            onChange={onSelect}
            canAdd={canAddMore}
            isDisabled={isDisabled}
            maxIndividualCount={maxIndividualCount}
            renderOptionLabel={renderAddonLabel({ withPrice: false })}
            renderButtonLabel={renderAddonPrice({ contrastLevel: `normal` })}
          />
        )

      default:
        return null
    }
  }

  const messageMeta = getMessageParameters({ maxTotalCount, minTotalCount })
  const validationMessage = getValidationMessage(messageMeta)

  const status = React.useMemo<TStatus>(() => {
    const isOverMinCount = minTotalCount === NOT_SET || selectedAddonCount >= minTotalCount
    const isNotOverMaxCount = maxTotalCount === NOT_SET || selectedAddonCount <= maxTotalCount

    return isOverMinCount && isNotOverMaxCount ? `success` : `error`
  }, [maxTotalCount, minTotalCount, selectedAddonCount])

  return (
    <Stack spacing={`smallMedium`}>
      <Skeleton showSkeleton={showSkeleton} maxWidth={`15rem`}>
        <H3 fontSize={`m18`} fontStyle={`boldSemi`}>
          {nameLabel}
        </H3>
      </Skeleton>

      {validationMessage && (
        <Skeleton showSkeleton={showSkeleton}>
          <AddonValidationMessage validationMessage={validationMessage} status={status} />
        </Skeleton>
      )}

      {renderAddonGroup()}
    </Stack>
  )
}
